import { useCallback, useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useGetContacts } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetContacts";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import {
    createNewContact,
    getSearchStringErrorVariant,
    mapContactsWithSelectionState,
    MAX_DISPLAYED_CONTACTS,
    mergeSelectedWithSuggestions,
    parseContactString,
    sortContactsBySelected,
    stringifyContact,
    switchContactSelection,
} from "app/pages/sales-intelligence/pages/ai-agent/containers/Attendees/utils";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";

export const useAttendeesValue = ({ value, onChange }: { value: Contact[]; onChange: (value: Contact[]) => void }) => {
    const {
        params: { domain, id },
    } = useCurrentStateAndParams<{ domain: string; id?: string }>();

    const [filteredValue, setFilteredValue] = useState<Contact[]>(value);
    const [search, setSearch] = useState<string | undefined>();
    const [shouldClearSearch, setShouldClearSearch] = useState(false);
    const [errorType, setErrorType] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (id) {
            setSearch("");
            setShouldClearSearch(true);
            setErrorType(undefined);
        }
    }, [id]);

    const { data, isFetched, isLoading } = useGetContacts({ domain, search });

    const handleSearchChange = useCallback(
        (searchTerm: string) => {
            setSearch(searchTerm);
            setErrorType(undefined);

            if (!searchTerm.trim()) {
                setFilteredValue(sortContactsBySelected(value));
            }
        },
        [value],
    );

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        if (shouldClearSearch) {
            timeoutId = setTimeout(() => {
                setShouldClearSearch(false);
            });
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [shouldClearSearch]);

    useEffect(() => {
        if (!search?.trim()) {
            if (value.length >= MAX_DISPLAYED_CONTACTS) {
                setFilteredValue(value);
            } else if (data && data.length > 0) {
                setFilteredValue(mergeSelectedWithSuggestions(value, data));
            } else {
                setFilteredValue(value);
            }
            return;
        }

        const isEmpty = (!data || data.length === 0) && isFetched;

        if (isEmpty) {
            const searchErrorType = isFetched ? getSearchStringErrorVariant(search) : undefined;
            setFilteredValue([]);
            setErrorType(searchErrorType);
            return;
        }

        if (data) {
            setFilteredValue(mapContactsWithSelectionState(data, value));
        }
    }, [search, value, data, isFetched]);

    const handleCheckboxClick = useCallback(
        (contact: Contact) => {
            const isContactSelected = contact.isSelected;
            let updatedContacts: Contact[] = [];

            if (isContactSelected) {
                AIAgentTrackingService.meeting.trackAttendeesCheckboxDeselect();
                updatedContacts = switchContactSelection(value, contact.id);
            } else {
                AIAgentTrackingService.meeting.trackAttendeesCheckboxSelect(
                    stringifyContact(contact),
                    !!contact.isSuggested,
                );
                if (!value.some((c) => c.id === contact.id)) {
                    updatedContacts = [createNewContact(contact), ...value];
                } else {
                    updatedContacts = switchContactSelection(value, contact.id);
                }
            }

            onChange(updatedContacts);
        },
        [value, onChange],
    );

    const handleAddNewContact = useCallback(() => {
        AIAgentTrackingService.meeting.trackAddNewAttendee(search ?? "");
        onChange([createNewContact(parseContactString(search ?? "")), ...value]);

        setSearch("");
        setShouldClearSearch(true);
        setErrorType(undefined);
    }, [search, value, onChange]);

    const handleSearchFocus = useCallback(() => {
        setErrorType(undefined);
    }, []);

    return {
        filteredValue,
        errorType,
        isButtonVisible: Boolean(search && search.trim().length > 0),
        shouldClearSearch,
        isLoading,
        handleSearchChange,
        handleCheckboxClick,
        handleAddNewContact,
        handleSearchFocus,
    };
};
